import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { API_URL } from "../../apiConfig";
import PartnerCardPreview from "../../components/PartnerCardPreview";
import { PencilSquareIcon, ArrowLeftIcon, EyeIcon, MagnifyingGlassIcon, ArrowRightIcon, ArrowDownTrayIcon, ArrowPathIcon, XCircleIcon, PhoneIcon, HashtagIcon } from "@heroicons/react/24/outline";

const T = {
  ink:        "#1a1a1c",
  ink2:       "#4a4a50",
  ink3:       "#86868b",
  ink4:       "#b4b3b0",
  hair:       "rgba(20,20,22,0.07)",
  hair2:      "rgba(20,20,22,0.04)",
  surface:    "rgba(255,255,255,0.72)",
  surface2:   "#f9f8f6",
  accent:     "#1e3a8a",
  accentSoft: "rgba(30,58,138,0.09)",
  ok:         "#1E8E3E",
  okSoft:     "rgba(30,142,62,0.10)",
  warn:       "#B25E09",
  warnSoft:   "rgba(178,94,9,0.10)",
  danger:     "#C5221F",
  dangerSoft: "rgba(197,34,31,0.10)",
  shadowMd:   "0 6px 24px -8px rgba(20,20,22,0.08),0 2px 6px rgba(20,20,22,0.04)",
  font:       "'Geist',-apple-system,BlinkMacSystemFont,'SF Pro Text',system-ui,sans-serif",
  r:          18,
};

const cardStyle = {
  background: T.surface,
  backdropFilter: "blur(28px) saturate(180%)",
  WebkitBackdropFilter: "blur(28px) saturate(180%)",
  border: `1px solid ${T.hair}`,
  borderRadius: T.r,
  boxShadow: T.shadowMd,
};

const LOGO_COLORS = ["#1e3a8a","#0f5c2e","#7c2d12","#4c1d95","#b45309","#0f766e","#be185d","#1d4ed8"];
function logoColor(name = "") {
  const n = (name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return LOGO_COLORS[n % LOGO_COLORS.length];
}
function logoInitials(name = "") {
  return (name || "").split(" ").filter(Boolean).slice(0, 2).map(p => p[0]).join("").toUpperCase() || "?";
}

const CHECK_AVATAR_FOUND    = { bg: "rgba(30,142,62,0.10)",  color: "#1E8E3E" };
const CHECK_AVATAR_NOTFOUND = { bg: "rgba(197,34,31,0.10)",  color: "#C5221F" };
function checkAvatar(isClient) { return isClient ? CHECK_AVATAR_FOUND : CHECK_AVATAR_NOTFOUND; }

function Field({ label, value, sub, mono, muted }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, color: T.ink3, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 500, marginBottom: 5 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 500, color: muted ? T.ink4 : T.ink, wordBreak: "break-word", fontVariantNumeric: mono ? "tabular-nums" : "normal" }}>{value || "—"}</div>
      {sub && <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 3, fontVariantNumeric: "tabular-nums" }}>{sub}</div>}
    </div>
  );
}

export default function PartnerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = useMemo(() => localStorage.getItem("token") || "", []);
  const apiBase = useMemo(() => API_URL.replace(/\/api\/?$/, ""), []);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [item, setItem] = useState(null);
  const [checks, setChecks] = useState([]);

  async function load() {
    if (!API_URL || !token || !id) return;
    setLoading(true); setErr("");
    try {
      const r = await fetch(`${API_URL}/admin/partners/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || "Falha ao carregar parceiro");
      setItem(data?.partner || null);
      const c = await fetch(`${API_URL}/admin/partners/${id}/checks?limit=20`, { headers: { Authorization: `Bearer ${token}` } });
      const cData = await c.json();
      if (c.ok) setChecks(Array.isArray(cData?.items) ? cData.items : []);
    } catch (e) { setErr(e?.message || "Erro ao carregar dados"); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [id]);

  const fmt = (d) => d ? new Date(d).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—";

  const monthChecks = useMemo(() => {
    const now = new Date();
    return checks.filter(c => {
      const d = new Date(c.createdAt);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
  }, [checks]);

  const lc = item?.color || logoColor(item?.company);

  if (loading) return (
    <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
      <Spinner animation="border" style={{ color: T.accent }} />
    </div>
  );

  if (err) return (
    <div style={{ padding: "12px 16px", borderRadius: 12, background: T.dangerSoft, color: T.danger, fontSize: 13, fontFamily: T.font }}>{err}</div>
  );

  if (!item) return (
    <div style={{ padding: "12px 16px", borderRadius: 12, background: T.hair2, color: T.ink3, fontSize: 13, fontFamily: T.font }}>Parceiro não encontrado.</div>
  );

  return (
    <div style={{ fontFamily: T.font }}>
      <style>{`
        .checks-tbl th { text-align:left; font-weight:600; font-size:10.5px; color:${T.ink2}; letter-spacing:.06em; text-transform:uppercase; padding:10px 14px; border-bottom:1px solid ${T.hair}; background:transparent; }
        .checks-tbl td { padding:11px 14px; vertical-align:middle; border-bottom:1px solid ${T.hair2}; font-size:13px; color:${T.ink}; }
        .checks-tbl tr:last-child td { border-bottom:none; }
      `}</style>

      {/* Breadcrumb */}
      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:16, fontSize:13 }}>
        <button onClick={() => navigate("/admin/partners")}
          style={{ display:"inline-flex", alignItems:"center", gap:5, background:"none", border:"none", color:T.ink2, cursor:"pointer", padding:"4px 8px", borderRadius:8, fontFamily:T.font, fontSize:13 }}>
          <ArrowLeftIcon style={{ width:13, height:13 }}/> Parceiros
        </button>
        <span style={{ color:T.ink4 }}>›</span>
        <span style={{ color:T.ink3 }}>Detalhes</span>
      </div>

      {/* Hero */}
      <div style={{ ...cardStyle, padding:24, marginBottom:14, position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", top:-60, right:-60, width:280, height:280, borderRadius:"50%", background:`radial-gradient(circle, ${lc}22, transparent 70%)`, filter:"blur(40px)", pointerEvents:"none" }}/>
        <div style={{ display:"flex", gap:20, alignItems:"center", position:"relative", flexWrap:"wrap" }}>
          <div style={{ width:72, height:72, borderRadius:20, background:lc, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, fontWeight:700, letterSpacing:"-0.02em", flexShrink:0, boxShadow:T.shadowMd }}>
            {logoInitials(item.company)}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:6 }}>
              <h1 style={{ margin:0, fontSize:26, fontWeight:600, letterSpacing:"-0.025em", color:T.ink }}>{item.company || "—"}</h1>
              {item.active
                ? <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", fontSize:11.5, fontWeight:500, borderRadius:999, background:T.okSoft, color:T.ok }}><span style={{ width:5, height:5, borderRadius:"50%", background:T.ok }}/>Ativo</span>
                : <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", fontSize:11.5, fontWeight:500, borderRadius:999, background:T.hair2, color:T.ink3 }}><span style={{ width:5, height:5, borderRadius:"50%", background:T.ink3 }}/>Inativo</span>}
              {item.category && <span style={{ padding:"3px 10px", borderRadius:999, fontSize:11.5, fontWeight:500, background:T.accentSoft, color:T.accent }}>{item.category}</span>}
              {typeof item.displayOrder === "number" && <span style={{ padding:"3px 8px", borderRadius:6, fontSize:11.5, fontWeight:600, background:T.hair2, color:T.ink2 }}>#{item.displayOrder}</span>}
            </div>
            <div style={{ fontSize:14, color:T.ink2, fontWeight:500 }}>{item.benefit || ""}</div>
          </div>
          <div style={{ display:"flex", gap:8, flexShrink:0 }}>
            <button onClick={() => window.open(`/consulta`, "_blank")}
              style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"8px 14px", borderRadius:10, border:`1px solid ${T.hair}`, background:"transparent", fontSize:13, color:T.ink2, cursor:"pointer", fontFamily:T.font }}>
              <EyeIcon style={{ width:14, height:14 }}/> Visualizar no app
            </button>
            <button onClick={() => navigate(`/admin/partners/${id}/edit`)}
              style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"8px 16px", borderRadius:10, border:"none", background:T.accent, fontSize:13, fontWeight:500, color:"#fff", cursor:"pointer", fontFamily:T.font }}>
              <PencilSquareIcon style={{ width:14, height:14 }}/> Editar
            </button>
          </div>
        </div>
      </div>

      {/* Body grid */}
      <div style={{ display:"grid", gridTemplateColumns:"minmax(0,1fr) 320px", gap:14, alignItems:"start" }}>
        {/* Left */}
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

          {/* Informações */}
          <div style={cardStyle}>
            <div style={{ padding:"14px 20px", borderBottom:`1px solid ${T.hair}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <h3 style={{ margin:0, fontSize:13, fontWeight:600, color:T.ink }}>Informações da empresa</h3>
            </div>
            <div style={{ padding:"20px 20px 0", display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:20 }}>
              <Field label="E-mail"          value={item.email}        mono />
              <Field label="Tipo"            value={roleLabel(item.role)} />
              <Field label="Categoria"       value={item.category} />
              <Field label="Nome de contato" value={item.contactName} />
              <Field label="Telefone"        value={item.phone}        sub={item.whatsappPhone ? `WhatsApp: ${item.whatsappPhone}` : undefined} mono />
              <Field label="Cidade"          value={item.city} />
            </div>
            <div style={{ margin:"16px 20px 0", height:1, background:T.hair }}/>
            <div style={{ padding:"16px 20px 20px", display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:20 }}>
              <div>
                <div style={{ fontSize:11, color:T.ink3, textTransform:"uppercase", letterSpacing:"0.04em", fontWeight:500, marginBottom:5 }}>Status</div>
                <div style={{ fontSize:13.5, fontWeight:500, color:item.active ? T.ok : T.ink3 }}>{item.active ? "Ativo" : "Inativo"}</div>
              </div>
              <Field label="Criado em"      value={fmt(item.createdAt)}     mono />
              <Field label="Atualizado em"  value={fmt(item.updatedAt)}     mono />
              <Field label="Último acesso"  value={fmt(item.lastLoginAt)}   mono muted={!item.lastLoginAt} />
            </div>
          </div>

          {/* Descrição */}
          {item.benefitDescription && (
            <div style={cardStyle}>
              <div style={{ padding:"14px 20px", borderBottom:`1px solid ${T.hair}` }}>
                <h3 style={{ margin:0, fontSize:13, fontWeight:600, color:T.ink }}>Descrição do benefício</h3>
              </div>
              <div style={{ padding:"16px 20px", fontSize:14, lineHeight:1.65, color:T.ink2 }}>{item.benefitDescription}</div>
            </div>
          )}

          {/* KPI stats */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14 }}>
            {[
              { label:"Consultas no mês",   value: monthChecks,              icon:<MagnifyingGlassIcon style={{ width:13, height:13 }}/>, tone:T.accent,  toneSoft:T.accentSoft },
              { label:"Consultas totais",   value: checks.length,            icon:<EyeIcon style={{ width:13, height:13 }}/>,             tone:"#7c3aed",  toneSoft:"rgba(124,58,237,0.10)" },
              { label:"Cliques WhatsApp",   value: "—",                      icon:<PhoneIcon style={{ width:13, height:13 }}/>,           tone:T.ok,       toneSoft:T.okSoft },
              { label:"Posição no ranking", value: typeof item.displayOrder==="number" ? `#${item.displayOrder}` : "—", icon:<HashtagIcon style={{ width:13, height:13 }}/>, tone:T.warn, toneSoft:T.warnSoft },
            ].map((kpi, i) => (
              <div key={i} style={{ ...cardStyle, padding:"16px 18px 14px" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                  <div style={{ fontSize:11.5, color:T.ink3, fontWeight:500 }}>{kpi.label}</div>
                  <div style={{ width:26, height:26, borderRadius:8, background:kpi.toneSoft, color:kpi.tone, display:"flex", alignItems:"center", justifyContent:"center" }}>{kpi.icon}</div>
                </div>
                <div style={{ fontSize:30, fontWeight:600, letterSpacing:"-0.04em", lineHeight:1, color:T.ink }}>{kpi.value}</div>
              </div>
            ))}
          </div>

          {/* Últimas consultas */}
          <div style={{ ...cardStyle, overflow:"hidden" }}>
            <div style={{ padding:"14px 20px", borderBottom:`1px solid ${T.hair}`, display:"flex", alignItems:"center", gap:8 }}>
              <MagnifyingGlassIcon style={{ width:14, height:14, color:T.ink3 }}/>
              <h3 style={{ margin:0, fontSize:13, fontWeight:600, color:T.ink }}>Últimas consultas</h3>
              <span style={{ fontSize:12, color:T.ink3 }}>· {checks.length} registradas</span>
            </div>
            {checks.length === 0 ? (
              <div style={{ padding:"40px 20px", textAlign:"center", color:T.ink3, fontSize:13 }}>Nenhuma consulta registrada.</div>
            ) : (
              <div style={{ overflowX:"auto" }}>
                <table className="checks-tbl" style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ paddingLeft:20 }}>Morador</th>
                      <th>Condomínio</th>
                      <th>Status</th>
                      <th style={{ paddingRight:20 }}>Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checks.map((c, i) => {
                      const av = checkAvatar(c.isClient);
                      const name = c.firstName || "—";
                      return (
                        <tr key={i}>
                          <td style={{ paddingLeft:20 }}>
                            <div style={{ display:"flex", alignItems:"center", gap:9 }}>
                              <div style={{ width:28, height:28, borderRadius:"50%", background:av.bg, color:av.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10.5, fontWeight:600, flexShrink:0 }}>
                                {logoInitials(name)}
                              </div>
                              <div>
                                <div style={{ fontWeight:500, color:T.ink }}>{name}</div>
                                {c.maskedDocument && <div style={{ fontSize:11, color:T.ink3 }}>{c.maskedDocument}</div>}
                              </div>
                            </div>
                          </td>
                          <td style={{ color:T.ink2 }}>{c.condoName || "—"}</td>
                          <td>
                            <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", fontSize:11.5, fontWeight:500, borderRadius:999, background:c.isClient ? T.okSoft : T.dangerSoft, color:c.isClient ? T.ok : T.danger }}>
                              <span style={{ width:5, height:5, borderRadius:"50%", background:c.isClient ? T.ok : T.danger }}/>
                              {c.isClient ? "Encontrado" : "Não encontrado"}
                            </span>
                          </td>
                          <td style={{ color:T.ink2, fontVariantNumeric:"tabular-nums", paddingRight:20 }}>
                            {c.createdAt ? new Date(c.createdAt).toLocaleString("pt-BR", { dateStyle:"short", timeStyle:"short" }) : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Right: preview + ações rápidas */}
        <div style={{ display:"flex", flexDirection:"column", gap:14, position:"sticky", top:16 }}>
          <div style={{ ...cardStyle, overflow:"hidden" }}>
            <div style={{ padding:"14px 16px 12px", borderBottom:`1px solid ${T.hair}`, display:"flex", alignItems:"center", gap:8 }}>
              <EyeIcon style={{ width:14, height:14, color:T.ink3 }}/>
              <div>
                <div style={{ fontSize:13, fontWeight:600, color:T.ink }}>Preview no app</div>
                <div style={{ fontSize:11.5, color:T.ink3, marginTop:1 }}>Como o morador vê o benefício</div>
              </div>
            </div>
            <div style={{ padding:16, background:T.hair2 }}>
              <PartnerCardPreview item={item} />
            </div>
          </div>

          {/* Ações rápidas */}
          <div style={{ ...cardStyle, padding:14 }}>
            <div style={{ fontSize:11, color:T.ink3, textTransform:"uppercase", letterSpacing:"0.04em", fontWeight:500, marginBottom:10, paddingLeft:4 }}>Ações rápidas</div>
            <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
              {[
                { icon:<ArrowPathIcon style={{ width:14, height:14 }}/>, label:"Resetar senha",        danger:false, action:() => navigate(`/admin/partners/${id}/edit`) },
                { icon:<XCircleIcon style={{ width:14, height:14 }}/>,  label: item.active ? "Desativar parceiro" : "Ativar parceiro", danger:true, action:() => navigate(`/admin/partners/${id}/edit`) },
              ].map((a, i) => (
                <button key={i} onClick={a.action}
                  style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", background:"transparent", border:"none", borderRadius:9, color:a.danger ? T.danger : T.ink, fontFamily:T.font, fontSize:13, fontWeight:500, cursor:"pointer", textAlign:"left", transition:"background 0.12s" }}
                  onMouseEnter={e => e.currentTarget.style.background = a.danger ? T.dangerSoft : T.hair2}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  {a.icon}
                  <span style={{ flex:1 }}>{a.label}</span>
                  <ArrowRightIcon style={{ width:12, height:12, opacity:0.4 }}/>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function roleLabel(role) {
  return (role || "").toLowerCase() === "admin" ? "admin" : "parceiro";
}
